import Backbone from 'backbone'
import template from './unsaved-warning.hbs'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import guardConstants from './guard-constants'
import BusyButton from 'scoreboard/components/busy-button'

// adapted from spider/unsavedchanges/Warning.js in SB2

var UnsavedWarning = Backbone.View.extend({

	className: 'dialog-message unsaved-warning',

	events: {
		'click .button-primary': 'yes',
		'click .dont-save': 'no',
		// #20328 to work with the dialog, this method needs to be called close
		'click .cancel': 'close'
	},

	/**
	 * @typedef UnsavedWarningMessages
	 * @property {String} [prompt=i18n.get('common.save.prompt')]
	 * @property {String} [promptWarning=i18n.get('common.save.promptWarning')]
	 * @property {String} [save=i18n.get('common.save')]
	 * @property {String} [dontSave=i18n.get('common.dontSave')]
	 */

	/**
	 * @param {UnsavedWarningMessages} [options.messages]
	 * @param {Boolean|Function} [options.hideCancel=false]
	 * @param {Function} [options.keepChangesFunction]
	 * @param {Function} [options.errorFunction] Default behavior is to just trigger cancel
	 * (the form view will typically show the error, but in the case of
	 * dependencies-prevent-action-view we need to keep the dialog open)
	 */
	initialize: function(options = {}) {
		this.options = options
		const messages = this.options.messages || {}
		this.messages = {
			prompt: messages.prompt || i18n.get('common.save.prompt'),
			promptWarning: messages.promptWarning || i18n.get('common.save.promptWarning'),
			save: messages.save || i18n.get('common.save'),
			dontSave: messages.dontSave || i18n.get('common.dontSave')
		}
		_.bindAll(this, 'close')
	},

	render: function() {
		this.$el.empty().append(template(this.messages))
		this.busyButton = new BusyButton({ el: this.$('.button-primary') })
		let hideCancel = typeof this.options.hideCancel === 'function' ?
			this.options.hideCancel() : this.options.hideCancel
		if (hideCancel) {
			this.$('.cancel').remove()
		}
		return this
	},

	/**
	 * Callback triggered when the user choose that Yes, they would like
	 * to save their changes.
	 */
	// aka spider/unsavedchanges/Warning.js:keepChanges in SB2
	yes: function() {
		// We aren't using the formView for this prompt, so we will manually add the class
		// that disables/lowers the opacity of the dialog content.
		this.$el.addClass('is-submitting-form')
		this.busyButton.loading()
		var keepChangesResult = _.result(this.options, 'keepChangesFunction')
		Promise.resolve(keepChangesResult).then(result => {
			// unless explicitly asked to hide the warning and not continue
			if (result === guardConstants.HIDE_WARNING) {
				this.trigger('cancel')
			} else {
				this.trigger('done')
				// go ahead and continue
				this.continue()
			}
			_.result(this.options, 'yesFunction')
		}, error => {
			const {actionFailedReason} = _.get(error, 'responseJSON')
			// Must trigger `error` or `cancel` so the guard can clean itself up
			// (click events in the head pane stop working otherwise)
			if (this.options.errorFunction) {
				this.options.errorFunction(error)
				this.trigger('error', error)
			} else if (actionFailedReason) {
				// for `actionFailedReason` see:
				// DomainObjectDependencyExceptionMessage.java
				// dependencies-prevent-action-handler.js
				this.trigger('error', error)
			} else {
				// #26748 If a XHR fails during a save guard, and there is no
				// error handler it must be dismissed
				this.trigger('cancel')
			}
		})
	},

	/**
	 * Callback triggered when the user chooses that No, they would not like
	 * to save their changes.
	 */
	// aka spider/unsavedchanges/Warning.js:discardChanges in SB2
	no: function() {
		// #9779 trigger 'done' before continuing; otherwise, if continue() opens
		// a dialog, that dialog will be popped instead of the warning dialog
		this.trigger('done')
		this.continue()
		_.result(this.options, 'noFunction')
	},

	/**
	 * After the user chooses Yes or No, continues on with whatever the user
	 * previously had chosen to do (e.g. go to the page the user clicked on)
	 */
	'continue': function() {
		_.result(this.options, 'continueFunction')
		this.trigger('continue')
	},

	// when the user cancels the dialog by pressing cancel or pressing the
	// escape key.  appDisplay._wireDialog ensures when the dialog experiences
	// a cancel event, a cancel event is triggered on this view, and
	// in the initialize method we listen for that to trigger this method
	close: function() {
		_.result(this.options, 'cancelFunction')
		// #19730 trigger a cancel event so cleanup can happen
		this.trigger('cancel')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.busyButton], 'remove')
	}

})

export default UnsavedWarning

UnsavedWarning.__filename__ = 'components/unsaved/unsaved-warning.js'
